import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Moment } from 'moment'
import { useQuery, useMutation } from '@apollo/client'
import { MAILS_TO_PROVISIONED, VALIDATE_USER_MAIL_PROVISIONING_BATCHS } from 'graphql/mails/queries'
import Mail from 'globals/models/Mail'
import MailDetails from 'pages/ClientDetails/components/MailDetails'
import popupSuccess from 'globals/popupSuccess'
import { MailWrapper } from './styles'
import SelectList from '../components/SelectList'
import Topbar from '../components/Topbar'
import { ActionBar } from '../components/styles'
import SelectedMailsButtons from 'components/SelectedMailsButtons'

interface IProps {
  date: Moment,
  setDate: (date: Moment | null) => void
}

const Mails = (props: IProps) => {
  const { t } = useTranslation()
  const { confirm } = Modal
  const { date, setDate } = props

  const [showModal, setShowModal] = useState<boolean>(false)
  const [focusMail, setFocusMail] = useState<Mail | undefined>()
  const [selectedMailsIds, setSelectedMailsIds] = useState<React.Key[]>([])

  const { loading, data } = useQuery(
      MAILS_TO_PROVISIONED,
      { variables: { date: date.toISOString() }, fetchPolicy: 'no-cache' }
  )
  const [validateUserMailProvisioningBatchs] = useMutation(VALIDATE_USER_MAIL_PROVISIONING_BATCHS)

  const userMailsByDomiciliation = data?.userMailsToProvision?.userMailsByDomiciliation
  const totalCount = data?.userMailsToProvision?.totalCount

  const validate = (receivedIds: React.Key[], notReceivedIds: React.Key[]) => {
    validateUserMailProvisioningBatchs({
      variables: {
        receivedUserMailIds: receivedIds,
        notReceivedUserMailIds: notReceivedIds
      }
    }).then(() => setDate(null)).then(() => popupSuccess(t('mails.provisionings.successfully-provisioned')))
  }

  const showConfirm = (receivedIds: React.Key[], notReceivedIds: React.Key[]) => {
    confirm({
      title: t('mails.provisionings.confirmModal.title'),
      icon: <ExclamationCircleOutlined />,
      content: t('mails.provisionings.confirmModal.content', { ids: notReceivedIds.toString() }),
      cancelText: t('global.no'),
      okText: t('global.yes'),
      okType: 'danger',
      onOk() {
        validate(receivedIds, notReceivedIds)
      }
    })
  }

  const onConfirm = (ids: React.Key[]) => {
    const notReceivedIds = userMailsByDomiciliation.map((obj: any) => obj.userMails).flat()
        .filter((mail: Mail) => !ids.includes(mail.id))
        .map((mail: Mail) => mail.id)

    notReceivedIds.length === 0 ? validate(ids, notReceivedIds) : showConfirm(ids, notReceivedIds)
  }

  if (!userMailsByDomiciliation || loading) {
    return (<></>)
  }

  return (
    <MailWrapper>
      <Topbar
        totalCount={data?.userMailsToDestroy?.pagination?.totalItemsCount || 1}
      />
      <ActionBar>
        <SelectedMailsButtons
          selectedMailsNumber={selectedMailsIds.length}
          confirmText={t('mails.provisionings.confirm')}
          onConfirm={() => onConfirm(selectedMailsIds)}
          onCancel={() => setSelectedMailsIds([])}
        />
      </ActionBar>
      <SelectList
        userMailsByDomiciliation={userMailsByDomiciliation}
        totalCount={totalCount}
        onMailClicked={(mail: Mail) => {
          setFocusMail(mail)
          setShowModal(true)
        }}
        defaultChecked
        lineStyle={'agenda'}
        selectedMailsIds={selectedMailsIds}
        setSelectedMailsIds={setSelectedMailsIds}
      />
      {
        focusMail &&
        <MailDetails
          mail={focusMail}
          visible={showModal}
          onClose={() => setShowModal(false)}
        />
      }
    </MailWrapper>
  )
}

export default Mails
